import React, { forwardRef, useEffect, useState } from 'react'

import { DatableTrace, DatableTraceDuration } from '@ui/interfaces'
import { generateUniqueColor } from '@ui/lib/utils'

interface WaterfallChartProps {
  traces: DatableTraceDuration[]
  totalDuration: number
  setSelectedRootTrace: (trace: DatableTrace) => void
}

interface TraceDurationExtended extends DatableTraceDuration {
  height: number
  width: number
  marginLeft: number
  color: string
}

export const WaterfallChart = forwardRef<HTMLDivElement, WaterfallChartProps>(
  ({ traces, totalDuration, setSelectedRootTrace }, ref) => {
    const [traceBars, setTraceBars] = useState<TraceDurationExtended[]>([])
    const containerRef = ref as React.MutableRefObject<HTMLDivElement | null>

    useEffect(() => {
      if (!containerRef.current) return

      const durationToWidth = (duration: number) => {
        const viewportWidth = containerRef.current?.clientWidth ?? 0
        const emInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize) * 20
        const availableWidth = viewportWidth - emInPixels
        const width = (duration / totalDuration) * availableWidth

        return width
      }

      let previousBarWidth = 0

      const t = traces.map((trace) => {
        const newItem = {
          ...trace,
          height: 20,
          width: durationToWidth(trace.duration),
          marginLeft: previousBarWidth,
          color: generateUniqueColor(trace.record.resource['service.name']?.length ?? 0),
        }
        previousBarWidth += newItem.width

        return newItem
      })

      setTraceBars(t)
    }, [traces, totalDuration, containerRef])

    return (
      <div ref={containerRef} className="flex max-h-[20vh] flex-row overflow-auto border">
        <div className="flex w-full flex-col">
          {traceBars.map((trace, index) => (
            <div
              key={index}
              className="group flex cursor-pointer flex-row border-b px-2 hover:bg-gray-100"
              onClick={() => setSelectedRootTrace(trace.record)}>
              <div className="flex w-[15rem] flex-col items-start border-r p-2 text-left">
                <p className="w-full truncate font-medium">{trace.record.resource['service.name']}</p>
                <p className="w-full truncate">{trace.record.name}</p>
                <p>{trace.duration}ms</p>
              </div>

              <div className="flex items-center justify-center bg-white pl-2 group-hover:bg-gray-100">
                <div
                  style={{
                    width: trace.width,
                    minWidth: '.25rem',
                    backgroundColor: trace.color,
                    marginLeft: trace.marginLeft,
                  }}
                  className="h-1/4 rounded-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
)
